// ClienteFormXL - Sprint 44 Fase 3 Vista 2.
// Formulario de cliente XL. Reemplaza src/modules/clientes/form/ClienteForm.jsx.
// Header con back/titulo/estado/acciones (Guardar + Activar/Inactivar).
// Tabs con XLChip (seleccionable). Render condicional por tab visible.
// Usa useClienteFormXL (con fixes heredados aplicados).
import { useEffect, useMemo } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useClienteFormXL from './hooks/useClienteFormXL';
import { TABS, TAB_LABELS, esColombia } from '../clientes/form/utils';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import XLSkeleton from '../../components/xl/XLSkeleton';
import DatosTabXL from './tabs/DatosTabXL';
import ComercialTabXL from './tabs/ComercialTabXL';
import ImpuestosTabXL from './tabs/ImpuestosTabXL';
import FiscalCOTabXL from './tabs/FiscalCOTabXL';
import RetencionesCOTabXL from './tabs/RetencionesCOTabXL';

const CSS_ID = 'xl-cli-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-form__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-form__head-left {
  display: flex; align-items: center; gap: 14px; min-width: 0;
}
.xl-form__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-form__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-form__back:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-form__title { margin: 0; font-size: 20px; font-weight: 600; color: var(--xl-color-text); line-height: 1.2 }
.xl-form__subtitle { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }

.xl-form__actions { display: flex; gap: 8px; align-items: center }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1), border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn--danger {
  background: transparent; color: var(--xl-color-danger);
  border-color: rgba(220,38,38,0.35);
}
.xl-btn--danger:hover { background: rgba(220,38,38,0.08) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-form__card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
}
.xl-form__tabs {
  display: flex; gap: 6px; flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
}
.xl-form__body { padding: 20px }
.xl-form__loading { padding: 20px; display: flex; flex-direction: column; gap: 10px }
`;

export default function ClienteFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const fm = useClienteFormXL();

  const tabsVisibles = useMemo(() => TABS.filter((k) => {
    if (k === 'fiscal_co' || k === 'retenciones_co') return esColombia(fm.form.pais);
    return true;
  }), [fm.form.pais]);

  // Si el tab activo deja de ser visible (cambio de pais fuera de Colombia),
  // caer a 'datos' para no mostrar vacio.
  useEffect(() => {
    if (!tabsVisibles.includes(fm.tab)) fm.setTab('datos');
  }, [tabsVisibles, fm]);

  if (!fm.loaded) {
    return (
      <div className="xl-page">
        <div className="xl-form__loading" aria-busy="true">
          <XLSkeleton variant="line" width="40%" height={18} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={200} />
        </div>
      </div>
    );
  }

  const titulo = fm.isNew ? 'Nuevo cliente' : (fm.form.nombre || 'Cliente');
  const subtitulo = fm.isNew ? 'Registrar cliente' : 'Editar cliente';
  const disabledActivo = fm.isNew || fm.acting;

  return (
    <div className="xl-page">
      <header className="xl-form__head">
        <div className="xl-form__head-left">
          <button
            type="button"
            className="xl-form__back"
            onClick={() => fm.navigate('/clientes')}
          >
            {'\u2190 Volver'}
          </button>
          <div>
            <h2 className="xl-form__title">{titulo}</h2>
            <p className="xl-form__subtitle">{subtitulo}</p>
          </div>
          {!fm.isNew && (
            <XLBadge variant={fm.form.is_active ? 'success' : 'neutral'} dot>
              {fm.form.is_active ? 'Activo' : 'Inactivo'}
            </XLBadge>
          )}
        </div>
        <div className="xl-form__actions">
          {!fm.isNew && (
            <button
              type="button"
              className={'xl-btn ' + (fm.form.is_active ? 'xl-btn--danger' : 'xl-btn--ghost')}
              onClick={fm.onToggleActivo}
              disabled={disabledActivo}
            >
              {fm.acting
                ? 'Procesando\u2026'
                : (fm.form.is_active ? 'Inactivar' : 'Activar')}
            </button>
          )}
          <button
            type="button"
            className="xl-btn xl-btn--primary"
            onClick={fm.onSave}
            disabled={fm.saving}
          >
            {fm.saving ? 'Guardando\u2026' : (fm.isNew ? 'Crear' : 'Guardar')}
          </button>
        </div>
      </header>

      {fm.msg && (
        <div
          className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}
        >
          {fm.msg.text}
        </div>
      )}

      <div className="xl-form__card">
        <div className="xl-form__tabs" role="tablist" aria-label="Secciones del cliente">
          {tabsVisibles.map((k) => (
            <XLChip
              key={k}
              onClick={() => fm.setTab(k)}
              selected={fm.tab === k}
            >
              {TAB_LABELS[k]}
            </XLChip>
          ))}
        </div>
        <div className="xl-form__body">
          {fm.tab === 'datos'          && <DatosTabXL          form={fm.form} set={fm.set} />}
          {fm.tab === 'comercial'      && <ComercialTabXL      form={fm.form} set={fm.set} />}
          {fm.tab === 'impuestos'      && <ImpuestosTabXL      form={fm.form} set={fm.set} />}
          {fm.tab === 'fiscal_co'      && <FiscalCOTabXL       form={fm.form} set={fm.set} />}
          {fm.tab === 'retenciones_co' && <RetencionesCOTabXL  form={fm.form} set={fm.set} />}
        </div>
      </div>
    </div>
  );
}
